import { InspectorTabsAdapter } from "../../adapters/InspectorTabsAdapter";

export class CmsInspectorTabs {
    static readonly #ADVANCED_NAMES = [
        "page_key",
        "template",
        "sort_order",
        "slug",
        "section_key",
        "element_key",
    ];

    static readonly #ADVANCED_EXCEPTIONS = [
        "board_slug",
    ];

    static enhance(scope: HTMLElement): void {
        this.#moveAdvancedFields(scope);
        this.#buildContextTabs(scope);
        this.#buildPageTabs(scope);
        scope.querySelectorAll<HTMLElement>("[data-cms-advanced-field]").forEach((field) => {
            field.closest<HTMLElement>(".admin-form__field")?.classList.add("admin-cms-inspector__advanced-field");
        });
        InspectorTabsAdapter.init(scope);
    }

    static #buildContextTabs(scope: HTMLElement): void {
        const sectionForm = scope.querySelector<HTMLElement>("[data-cms-section-form]");
        const contextCard = sectionForm?.querySelector<HTMLElement>("[data-cms-context-inspector]");
        if (!sectionForm || !contextCard || sectionForm.querySelector("[data-cms-inspector-tabs]")) return;

        const tabs = this.#createShell("section", contextCard.dataset.contextType ?? "section");
        const design = tabs.querySelector<HTMLElement>('[data-cms-inspector-panel-scroll="design"]');
        const data = tabs.querySelector<HTMLElement>('[data-cms-inspector-panel-scroll="data"]');
        const prototype = tabs.querySelector<HTMLElement>('[data-cms-inspector-panel-scroll="prototype"]');
        if (!design || !data || !prototype) return;

        contextCard.insertAdjacentElement("afterend", tabs);
        const candidates = [...sectionForm.children].filter((child): child is HTMLElement => {
            return child instanceof HTMLElement
                && child !== contextCard
                && child !== tabs
                && child.tagName.toLowerCase() !== "script";
        });

        candidates.forEach((child) => {
            const target = this.#targetForSectionChild(child);
            (target === "data" ? data : target === "prototype" ? prototype : design).appendChild(child);
        });

        this.#ensureEmptyState(prototype, "아직 상호작용이 설정되지 않았습니다.");
    }

    static #buildPageTabs(scope: HTMLElement): void {
        const pageInspector = scope.querySelector<HTMLElement>("[data-cms-page-inspector]");
        if (!pageInspector || pageInspector.querySelector("[data-cms-inspector-tabs]")) return;

        const legacyTabs = pageInspector.querySelector<HTMLElement>("[data-cms-page-legacy-tabs]");
        const activeTab = pageInspector.dataset.currentPageTab ?? "";
        const shell = this.#createShell("page", "page");
        const design = shell.querySelector<HTMLElement>('[data-cms-inspector-panel-scroll="design"]');
        const data = shell.querySelector<HTMLElement>('[data-cms-inspector-panel-scroll="data"]');
        const prototype = shell.querySelector<HTMLElement>('[data-cms-inspector-panel-scroll="prototype"]');
        if (!design || !data || !prototype) return;

        const contextCard = pageInspector.querySelector<HTMLElement>(".admin-cms-context-inspector");
        legacyTabs?.remove();
        if (contextCard) {
            contextCard.insertAdjacentElement("afterend", shell);
        } else {
            pageInspector.prepend(shell);
        }
        const children = [...pageInspector.children].filter((child): child is HTMLElement => {
            return child instanceof HTMLElement && child !== shell && child !== contextCard;
        });
        children.forEach((child) => {
            if (activeTab === "advanced") {
                data.appendChild(child);
            } else if (activeTab === "seo" || activeTab === "media" || activeTab === "basic") {
                design.appendChild(child);
            } else {
                design.appendChild(child);
            }
        });
        this.#prependPageDataSummary(data, pageInspector);
        this.#ensureEmptyState(prototype, "아직 페이지 전환이 설정되지 않았습니다.");
    }

    static #createShell(target: string, context: string): HTMLElement {
        const root = document.createElement("div");
        root.className = `admin-cms-inspector-tabs admin-cms-inspector-tabs--${target}`;
        root.dataset.cmsInspectorTabs = "1";
        root.dataset.inspectorContext = context;
        const uid = `cms-inspector-${target}-${Math.random().toString(36).slice(2, 8)}`;
        root.innerHTML = `
            <div class="admin-cms-inspector-tabs__rail" role="tablist" aria-label="Inspector panels">
                ${this.#tabButton(uid, "design", "Design", true)}
                ${this.#tabButton(uid, "data", "Data", false)}
                ${this.#tabButton(uid, "prototype", "Prototype", false)}
            </div>
            <div class="admin-cms-inspector-tabs__viewport" data-cms-inspector-tab-panels>
                <div class="admin-cms-inspector-tabs__wrapper">
                    ${this.#panel(uid, "design")}
                    ${this.#panel(uid, "data")}
                    ${this.#panel(uid, "prototype")}
                </div>
            </div>
        `;
        return root;
    }

    static #tabButton(uid: string, key: string, label: string, active: boolean): string {
        return `<button type="button" class="admin-cms-inspector-tabs__tab${active ? " is-active" : ""}" role="tab" id="${uid}-tab-${key}" aria-controls="${uid}-panel-${key}" aria-selected="${active ? "true" : "false"}" tabindex="${active ? "0" : "-1"}" data-cms-inspector-tab="${key}">${label}</button>`;
    }

    static #panel(uid: string, key: string): string {
        return `<section class="admin-cms-inspector-tabs__panel${key === "design" ? " is-active" : ""}" role="tabpanel" id="${uid}-panel-${key}" aria-labelledby="${uid}-tab-${key}" aria-hidden="${key === "design" ? "false" : "true"}" data-cms-inspector-panel="${key}"><div class="admin-cms-inspector-tabs__panel-scroll" data-lenis-prevent data-cms-inspector-panel-scroll="${key}"></div></section>`;
    }

    static #targetForSectionChild(child: HTMLElement): "design" | "data" | "prototype" {
        const text = child.textContent?.toLowerCase() ?? "";
        if (child.matches("[data-cms-template-field-inspector]")) {
            return "design";
        }
        if (
            child.matches("[data-template-binding], .cms-template-binding, .cms-board-preview-settings, [data-collection-preview]")
            || child.querySelector("[data-template-binding], .cms-template-binding, .cms-board-preview-settings, [data-collection-preview]")
            || text.includes("data binding")
            || text.includes("데이터 소스")
            || text.includes("게시판 데이터")
            || text.includes("field browser")
            || text.includes("필드 브라우저")
        ) {
            return "data";
        }
        if (text.includes("link") || text.includes("autoplay") || text.includes("prototype")) {
            return "prototype";
        }
        return "design";
    }

    static #prependPageDataSummary(panel: HTMLElement, pageInspector: HTMLElement): void {
        const summary = document.createElement("div");
        summary.className = "admin-cms-inspector-group admin-cms-inspector-group--static";
        const template = pageInspector.dataset.pageTemplate ?? "default";
        summary.innerHTML = `
            <div class="admin-cms-inspector-group__head">
                <span>Data Mode</span>
                <i class="fa-solid fa-database" aria-hidden="true"></i>
            </div>
            <div class="admin-cms-inspector-group__body">
                <div class="admin-cms-property-row">
                    <span class="admin-cms-property-row__label">Static Page</span>
                    <span class="admin-cms-property-row__value">${template === "default" ? "Active" : "Available"}</span>
                </div>
                <div class="admin-cms-property-row">
                    <span class="admin-cms-property-row__label">Board Index Page</span>
                    <span class="admin-cms-property-row__value">index.php</span>
                </div>
                <div class="admin-cms-property-row">
                    <span class="admin-cms-property-row__label">Board Detail Page</span>
                    <span class="admin-cms-property-row__value">show.php</span>
                </div>
            </div>
        `;
        panel.prepend(summary);
    }

    static #ensureEmptyState(panel: HTMLElement, label: string): void {
        if (panel.children.length > 0) return;
        const empty = document.createElement("div");
        empty.className = "admin-cms-inspector-empty";
        empty.innerHTML = `<i class="fa-regular fa-circle-dot" aria-hidden="true"></i><span>${label}</span>`;
        panel.appendChild(empty);
    }

    static #moveAdvancedFields(scope: HTMLElement): void {
        const form = scope.querySelector<HTMLFormElement>("form");
        if (!form || form.querySelector("[data-cms-inspector-advanced]")) return;
        const advanced: HTMLElement[] = [];
        form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("input[name], select[name], textarea[name]").forEach((input) => {
            const name = input.name;
            if (this.#ADVANCED_EXCEPTIONS.includes(name)) return;
            if (!this.#ADVANCED_NAMES.some((key) => name.includes(key))) return;
            const field = input.closest<HTMLElement>(".admin-form__field, .admin-form__block, .admin-form__group");
            if (field && !advanced.includes(field)) advanced.push(field);
        });
        if (advanced.length === 0) return;

        const details = document.createElement("details");
        details.className = "admin-cms-inspector__advanced";
        details.dataset.cmsInspectorAdvanced = "1";
        const summary = document.createElement("summary");
        summary.textContent = "고급 설정";
        const body = document.createElement("div");
        body.className = "admin-cms-inspector__advanced-body";
        advanced.forEach((field) => {
            field.classList.add("admin-cms-inspector__advanced-field");
            body.appendChild(field);
        });
        details.appendChild(summary);
        details.appendChild(body);
        form.appendChild(details);
    }
}
